<template>
  <div class="ix-page">
    <div class="ix-shell">
      <!-- ── Sidebar (Slack-style) ─────────────────────────────────── -->
      <aside class="ix-sidebar" :class="{ 'is-open': sidebarOpen }">
        <header class="ix-sidebar__head">
          <span class="ix-sidebar__accent" aria-hidden="true"></span>
          <div class="ix-sidebar__head-text">
            <h1>Inbox</h1>
            <p>Privileged sign-offs &amp; alerts</p>
          </div>
          <button
            type="button"
            class="ix-sidebar__toggle"
            aria-label="Collapse sidebar"
            @click="sidebarOpen = !sidebarOpen"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
        </header>

        <nav class="ix-nav" aria-label="Inbox navigation">
          <div class="ix-nav__group">
            <div class="ix-nav__group-label">Categories</div>
            <button
              v-for="cat in categories"
              :key="cat.key"
              type="button"
              class="ix-nav__item"
              :class="{ 'is-active': activeCategory === cat.key }"
              :aria-current="activeCategory === cat.key ? 'page' : undefined"
              @click="activeCategory = cat.key"
            >
              <span class="ix-nav__icon" aria-hidden="true" v-html="cat.icon"></span>
              <span class="ix-nav__label">{{ cat.label }}</span>
              <span
                v-if="categoryCounts[cat.key] > 0"
                class="ix-nav__count"
              >{{ categoryCounts[cat.key] }}</span>
            </button>
          </div>

          <div v-if="!isPaymentView" class="ix-nav__group">
            <div class="ix-nav__group-label">Status</div>
            <button
              v-for="s in statuses"
              :key="s.key"
              type="button"
              class="ix-nav__item ix-nav__item--status"
              :class="{ 'is-active': activeStatus === s.key }"
              @click="activeStatus = s.key"
            >
              <span class="ix-nav__dot" :class="`ix-nav__dot--${s.key}`" aria-hidden="true"></span>
              <span class="ix-nav__label">{{ s.label }}</span>
              <span
                v-if="statusCounts[s.key] > 0"
                class="ix-nav__count ix-nav__count--muted"
              >{{ statusCounts[s.key] }}</span>
            </button>
          </div>
        </nav>
      </aside>

      <!-- ── Main pane ─────────────────────────────────────────────── -->
      <main class="ix-main">
        <header class="ix-main__head">
          <button
            type="button"
            class="ix-main__hamburger"
            aria-label="Open sidebar"
            @click="sidebarOpen = true"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <div class="ix-main__title">
            <h2>
              <span class="ix-main__title-icon" aria-hidden="true" v-html="activeCategoryDef.icon"></span>
              {{ activeCategoryDef.label }}
            </h2>
            <p v-if="isPaymentView" class="ix-main__meta">
              {{ payments.count }} pending
              <span class="ix-main__dot">·</span>
              {{ formatINR(payments.sum) }}
            </p>
            <p v-else class="ix-main__meta">
              {{ activeStatusDef.label }}
              <span class="ix-main__dot">·</span>
              {{ visibleItems.length }} {{ visibleItems.length === 1 ? 'item' : 'items' }}
            </p>
          </div>
          <button
            type="button"
            class="ix-refresh-btn"
            :class="{ 'is-loading': headerLoading }"
            :disabled="headerLoading"
            @click="onRefresh"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ix-refresh-btn__icon" aria-hidden="true">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
          </button>
        </header>

        <CAlert v-if="errorMessage" color="danger" show class="ix-alert">{{ errorMessage }}</CAlert>
        <CAlert v-if="successMessage" color="success" show class="ix-alert">{{ successMessage }}</CAlert>

        <!-- ══════════════ PAYMENT APPROVALS VIEW ══════════════ -->
        <div v-if="isPaymentView" class="ix-pay">
          <section class="ix-pay-filters">
            <div class="ix-pay-filter">
              <label class="ix-pay-label">From Date</label>
              <SingleDatePicker :value="payments.fromDate" @input="payments.fromDate = $event" />
            </div>
            <div class="ix-pay-filter">
              <label class="ix-pay-label">To Date</label>
              <SingleDatePicker :value="payments.toDate" @input="payments.toDate = $event" />
            </div>
            <div class="ix-pay-filter">
              <label class="ix-pay-label">Payment Method</label>
              <AppDropdown
                :value="payments.method"
                :options="paymentMethodFilterOptions"
                @input="payments.method = $event"
              />
            </div>
            <div class="ix-pay-filter ix-pay-filter--action">
              <label class="ix-pay-label" aria-hidden="true">&nbsp;</label>
              <button type="button" class="ix-btn ix-btn--primary" :disabled="payments.loading" @click="loadPaymentApprovals">
                {{ payments.loading ? 'Loading…' : 'Apply' }}
              </button>
            </div>
            <div class="ix-pay-filter ix-pay-filter--action">
              <label class="ix-pay-label" aria-hidden="true">&nbsp;</label>
              <button type="button" class="ix-btn ix-btn--ghost" :disabled="payments.loading" @click="resetPaymentFilters">
                Reset
              </button>
            </div>
          </section>

          <BaseTable
            title="Pending Payments"
            :items="payments.results"
            :columns="paymentColumns"
            :loading="payments.loading"
            :sortable="false"
            :show-count="false"
            empty-text="No payment-pending orders for the selected filters."
            :paginated="true"
            :page-size="25"
          >
            <template #cell-payment_method="{ value }">
              <span :class="['ix-pill', methodPillClass(value)]">{{ formatLabel(value) }}</span>
            </template>
            <template #cell-grand_total="{ value }">{{ formatINR(value) }}</template>
            <template #cell-action="{ row }">
              <div class="ix-pay-row-actions">
                <button type="button" class="ix-pay-act ix-pay-act--approve" @click="openPaymentDetail(row)">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <span>Review &amp; Approve</span>
                </button>
              </div>
            </template>
          </BaseTable>
        </div>

        <section v-else class="ix-list-card">
          <div v-if="isLoading && !items.length" class="ix-list-state">
            <div class="ix-spinner" aria-hidden="true"></div>
            <p>Loading inbox…</p>
          </div>
          <div v-else-if="!visibleItems.length" class="ix-list-state ix-list-state--empty">
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
              <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
            </svg>
            <p>{{ emptyMessage }}</p>
          </div>
          <ul v-else class="ix-list">
            <li
              v-for="row in visibleItems"
              :key="row.id"
              class="ix-item"
              :class="{ 'ix-item--open': row.status === 'open' }"
              @click="openDetail(row)"
            >
              <span class="ix-item__bar" :class="barClass(row)" aria-hidden="true"></span>
              <div class="ix-item__body">
                <div class="ix-item__head">
                  <span :class="['ix-pill', typePillClass(row.type)]">{{ formatType(row.type) }}</span>
                  <h3 class="ix-item__title">{{ row.title }}</h3>
                  <span :class="['ix-pill', 'ix-item__status', displayStatusClass(row)]">
                    {{ displayStatusLabel(row) }}
                  </span>
                </div>
                <p class="ix-item__summary">{{ row.summary || '—' }}</p>
              </div>
              <div class="ix-item__aside">
                <span class="ix-item__time" :title="formatDateTime(row.created_at)">{{ formatRelative(row.created_at) }}</span>
                <button
                  type="button"
                  class="ix-item__action"
                  @click.stop="openDetail(row)"
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <span>{{ rowActionLabel(row) }}</span>
                </button>
              </div>
            </li>
          </ul>
        </section>
      </main>
    </div>

    <!-- Backdrop for mobile sidebar -->
    <div
      v-if="sidebarOpen"
      class="ix-sidebar__backdrop"
      @click="sidebarOpen = false"
    ></div>

    <CModal
      :show.sync="showDetail"
      size="lg"
      :centered="true"
      :title="detailTitle"
      class="ix-modal"
      :close-on-backdrop="!isResolving"
    >
      <div v-if="activeItem" class="vo-body">
        <div class="vo-section">
          <div class="vo-row">
            <span class="vo-key">Type</span>
            <span class="vo-val">
              <span :class="['ix-pill', typePillClass(activeItem.type)]">{{ formatType(activeItem.type) }}</span>
            </span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Status</span>
            <span class="vo-val">
              <span :class="['ix-pill', displayStatusClass(activeItem)]">
                {{ displayStatusLabel(activeItem) }}
              </span>
            </span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Summary</span>
            <span class="vo-val">{{ activeItem.summary || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Raised</span>
            <span class="vo-val">{{ formatDateTime(activeItem.created_at) }}</span>
          </div>
          <div v-if="activeItem.resolved_at" class="vo-row">
            <span class="vo-key">Resolved</span>
            <span class="vo-val">{{ formatDateTime(activeItem.resolved_at) }}</span>
          </div>
        </div>

        <template v-if="detailOrder">
          <div class="vo-grid">
            <div class="vo-row">
              <span class="vo-key">Order #</span>
              <span class="vo-val">{{ detailOrder.order_number || ('#' + detailOrder.id) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Order status</span>
              <span class="vo-val">{{ detailOrder.deleted_at ? 'Cancelled' : formatLabel(detailOrder.order_status) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Customer</span>
              <span class="vo-val">{{ detailOrder.contact_name || '—' }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Phone</span>
              <span class="vo-val">{{ detailOrder.contact_phone || '—' }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Order Type</span>
              <span class="vo-val">{{ formatLabel(detailOrder.order_type) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Order Date</span>
              <span class="vo-val">{{ orderDateDisplay(detailOrder) }}</span>
            </div>
            <div class="vo-row vo-row--wide">
              <span class="vo-key">Address</span>
              <span class="vo-val">{{ detailOrder.contact_address || '—' }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Post Office</span>
              <span class="vo-val">{{ detailOrder.contact_postofficename || '—' }}<span v-if="detailOrder.contact_pincode"> ({{ detailOrder.contact_pincode }})</span></span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Tracking ID</span>
              <span class="vo-val">{{ detailOrder.tracking_id || '—' }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Items</span>
              <span class="vo-val">{{ detailOrder.item_count != null ? detailOrder.item_count : detailOrderItems.length }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Subtotal</span>
              <span class="vo-val">₹{{ formatCurrency(detailOrder.total) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Discount</span>
              <span class="vo-val">−₹{{ formatCurrency(detailOrder.discount) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Discount %</span>
              <span class="vo-val">{{ discountPercentDisplay(detailOrder) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Shipping charge</span>
              <span class="vo-val">₹{{ formatCurrency(detailOrder.shipping_charge) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Grand total</span>
              <span class="vo-val"><strong>₹{{ formatCurrency(detailOrder.grand_total) }}</strong></span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Payment</span>
              <span class="vo-val">{{ formatLabel(detailOrder.payment_method) }} · {{ formatLabel(detailOrder.payment_status) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Shipping</span>
              <span class="vo-val">{{ formatLabel(detailOrder.shipping_method) }}</span>
            </div>
            <div class="vo-row">
              <span class="vo-key">Placed by</span>
              <span class="vo-val">{{ (detailOrder.user && detailOrder.user.name) || '—' }}</span>
            </div>
          </div>

          <div v-if="orderDetail.loading" class="ix-od-state">
            <div class="ix-spinner ix-spinner--sm" aria-hidden="true"></div>
            <span>Loading full order details…</span>
          </div>
          <div v-else-if="orderDetail.error" class="ix-od-error">{{ orderDetail.error }}</div>
          <div v-else-if="detailOrderItems.length" class="vo-items-wrap">
            <table class="vo-items">
              <thead>
                <tr><th>Product</th><th>Price</th><th>Qty</th><th>Total</th></tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in detailOrderItems" :key="`od-item-${idx}`">
                  <td>{{ item.name || item.item_name || '—' }}</td>
                  <td>{{ item.price != null ? item.price : '—' }}</td>
                  <td>{{ item.quantity != null ? item.quantity : '—' }}</td>
                  <td>{{ orderItemTotal(item) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div v-if="showInboxComplaint" class="vo-section ix-complaint">
          <div class="ix-complaint__head">
            <span class="ix-complaint__title">Post Office Complaint</span>
            <span :class="['ix-pill', complaint.filed ? 'ix-pill--green' : 'ix-pill--amber']">
              {{ complaint.filed ? 'Filed' : 'Not filed' }}
            </span>
          </div>

          <div v-if="complaint.error" class="ix-complaint__error">{{ complaint.error }}</div>

          <div class="ix-complaint__field">
            <div class="ix-complaint__label-row">
              <label class="ix-note-label">Draft message</label>
              <button type="button" class="ix-complaint__copy" @click="copyInboxComplaint">
                {{ complaint.copied ? 'Copied ✓' : 'Copy' }}
              </button>
            </div>
            <textarea v-model="complaint.draft" rows="5" class="ix-note-input"></textarea>
          </div>

          <div class="ix-complaint__grid">
            <div class="ix-complaint__field">
              <label class="ix-note-label">Complaint ID</label>
              <input
                v-model="complaint.id"
                type="text"
                class="ix-note-input ix-complaint__input"
                placeholder="e.g. PO/COD/2026/0456"
              />
            </div>
            <div class="ix-complaint__field">
              <label class="ix-note-label">Complaint Date</label>
              <SingleDatePicker :value="complaint.date" @input="complaint.date = $event" />
            </div>
          </div>

          <div class="ix-complaint__field">
            <label class="ix-note-label">Notes (optional)</label>
            <textarea
              v-model="complaint.notes"
              rows="2"
              class="ix-note-input"
              placeholder="Any extra context for this complaint…"
            ></textarea>
          </div>

          <div v-if="complaint.filed && complaint.by" class="ix-complaint__meta">
            Filed by <strong>{{ complaint.by }}</strong> on {{ complaintDateDisplay(complaint.date) }}
          </div>

          <div class="ix-complaint__actions">
            <button
              type="button"
              class="ix-btn ix-btn--primary"
              :disabled="complaint.saving || !complaint.id"
              @click="saveInboxComplaint"
            >
              {{ complaint.saving ? 'Saving…' : (complaint.filed ? 'Update Complaint' : 'Save Complaint') }}
            </button>
            <button
              v-if="complaint.filed"
              type="button"
              class="ix-btn ix-btn--ghost"
              :disabled="complaint.clearing"
              @click="clearInboxComplaint"
            >
              {{ complaint.clearing ? 'Clearing…' : 'Clear' }}
            </button>
          </div>
        </div>

        <div v-if="activeItem.subject && activeItem.subject_type === 'refund'" class="vo-grid">
          <div class="vo-row">
            <span class="vo-key">Refund #</span>
            <span class="vo-val">{{ activeItem.subject.id }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Order #</span>
            <span class="vo-val">{{ refundOrderNumber(activeItem) }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Refund status</span>
            <span class="vo-val">{{ formatLabel(activeItem.subject.status) }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Amount</span>
            <span class="vo-val"><strong>₹{{ formatCurrency(activeItem.subject.amount) }}</strong></span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Account name</span>
            <span class="vo-val">{{ activeItem.subject.name || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Bank A/C</span>
            <span class="vo-val">{{ activeItem.subject.bank_account_no || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">IFSC</span>
            <span class="vo-val">{{ activeItem.subject.IFSC || '—' }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Requested by</span>
            <span class="vo-val">{{ refundRequestedBy(activeItem) }}</span>
          </div>
          <div class="vo-row vo-row--wide">
            <span class="vo-key">Reason</span>
            <span class="vo-val">{{ activeItem.subject.reason || '—' }}</span>
          </div>
        </div>

        <div v-if="refundProofUrl(activeItem)" class="vo-section vo-section--proof">
          <div class="rf-proof-head">
            <span class="rf-proof-title">Payment Proof</span>
            <a class="rf-proof-open" :href="refundProofUrl(activeItem)" target="_blank" rel="noopener">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              <span>Open full size</span>
            </a>
          </div>
          <div class="rf-proof-preview">
            <img
              v-if="isRefundProofImage(activeItem)"
              :src="refundProofUrl(activeItem)"
              :alt="`Refund proof #${activeItem.subject && activeItem.subject.id}`"
              class="rf-proof-image"
              loading="lazy"
            />
            <iframe
              v-else-if="isRefundProofPdf(activeItem)"
              :src="refundProofUrl(activeItem)"
              class="rf-proof-pdf"
              title="Refund proof PDF"
              loading="lazy"
              referrerpolicy="no-referrer"
            ></iframe>
            <div v-else class="rf-proof-fallback">
              <span>Proof attached — preview not available for this file type.</span>
              <a :href="refundProofUrl(activeItem)" target="_blank" rel="noopener">Open in a new tab</a>
            </div>
          </div>
        </div>

        <div v-if="isActionable" class="vo-section">
          <div class="ix-note-wrap">
            <label class="ix-note-label">Note (optional)</label>
            <textarea
              v-model="resolveNote"
              rows="2"
              maxlength="500"
              class="ix-note-input"
              :disabled="isResolving"
              placeholder="Add a reason for approval or rejection — appears on the order's notes."
            />
          </div>
        </div>

        <div
          v-else-if="isSubjectCancelledUndecided(activeItem) && isApprovalItem(activeItem)"
          class="vo-section ix-cancelled-note"
        >
          This order is cancelled, so the request is void — no approval or rejection is needed.
        </div>
      </div>

      <template #footer>
        <button
          type="button"
          class="ix-btn ix-btn--ghost"
          :disabled="isResolving"
          @click="showDetail = false"
        >Close</button>
        <template v-if="isActionable">
          <button
            type="button"
            class="ix-btn ix-btn--danger"
            :disabled="isResolving"
            @click="confirmReject"
          >Reject</button>
          <button
            type="button"
            class="ix-btn ix-btn--primary"
            :disabled="isResolving"
            @click="confirmApprove"
          >{{ isResolving && pendingResolution === 'approve' ? 'Approving…' : 'Approve' }}</button>
        </template>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="confirmDialog.visible"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :confirm-label="confirmDialog.confirmLabel"
      cancel-label="Cancel"
      busy-label="Working…"
      :busy="isResolving"
      :variant="confirmDialog.variant"
      :close-on-backdrop="!isResolving"
      @confirm="runResolve"
      @cancel="confirmDialog.visible = false"
    />

    <AppConfirmDialog
      :visible="proofDelete.visible"
      title="Delete screenshot?"
      :message="proofDelete.message"
      confirm-label="Delete"
      cancel-label="Cancel"
      busy-label="Deleting…"
      :busy="payDetail.proofBusy"
      variant="danger"
      :close-on-backdrop="!payDetail.proofBusy"
      @confirm="confirmDeleteScreenshot"
      @cancel="proofDelete.visible = false"
    />

    <!-- Payment order detail — review + approve + update method + edit -->
    <CModal
      :show.sync="payDetail.show"
      size="lg"
      :centered="true"
      title="Order Details"
      class="ix-modal"
      :close-on-backdrop="!payDetail.approving && !payDetail.savingMethod"
    >
      <div v-if="payDetail.loading" class="ix-list-state">
        <div class="ix-spinner" aria-hidden="true"></div>
        <p>Loading order details…</p>
      </div>
      <div v-else-if="payDetail.error" class="ix-pd-error">{{ payDetail.error }}</div>
      <div v-else-if="payDetail.order" class="vo-body">
        <div class="vo-section">
          <div class="vo-row"><span class="vo-key">Order #</span><span class="vo-val">{{ payDetail.order.order_number || ('#' + payDetail.order.id) }}</span></div>
          <div class="vo-row"><span class="vo-key">Organisation</span><span class="vo-val">{{ payDetail.row && payDetail.row.organisation || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Customer</span><span class="vo-val">{{ payDetail.order.contact_name || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Phone</span><span class="vo-val">{{ payDetail.order.contact_phone || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Address</span><span class="vo-val">{{ payDetail.order.contact_address || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Post Office</span><span class="vo-val">{{ payDetail.order.contact_postofficename || '—' }}<span v-if="payDetail.order.contact_pincode"> ({{ payDetail.order.contact_pincode }})</span></span></div>
          <div class="vo-row"><span class="vo-key">Tracking ID</span><span class="vo-val">{{ payDetail.order.tracking_id || '—' }}</span></div>
        </div>

        <div class="vo-items-wrap">
          <table class="vo-items">
            <thead>
              <tr><th>Product</th><th>Price</th><th>Qty</th><th>Total</th></tr>
            </thead>
            <tbody>
              <tr v-if="!payDetail.order.order_items || !payDetail.order.order_items.length">
                <td colspan="4" class="vo-items__empty">No items</td>
              </tr>
              <tr v-for="(item, idx) in (payDetail.order.order_items || [])" :key="`pd-item-${idx}`">
                <td>{{ item.name || item.item_name || '—' }}</td>
                <td>{{ item.price != null ? item.price : '—' }}</td>
                <td>{{ item.quantity != null ? item.quantity : '—' }}</td>
                <td>{{ orderItemTotal(item) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="vo-grid">
          <div class="vo-row"><span class="vo-key">Order Type</span><span class="vo-val">{{ formatLabel(payDetail.order.order_type) }}</span></div>
          <div class="vo-row"><span class="vo-key">Order Date</span><span class="vo-val">{{ orderDateDisplay(payDetail.order) }}</span></div>
          <div class="vo-row"><span class="vo-key">Subtotal</span><span class="vo-val">₹{{ formatCurrency(payDetail.order.total) }}</span></div>
          <div class="vo-row"><span class="vo-key">Discount</span><span class="vo-val">−₹{{ formatCurrency(payDetail.order.discount) }}</span></div>
          <div class="vo-row"><span class="vo-key">Discount %</span><span class="vo-val">{{ discountPercentDisplay(payDetail.order) }}</span></div>
          <div class="vo-row"><span class="vo-key">Shipping</span><span class="vo-val">₹{{ formatCurrency(payDetail.order.shipping_charge) }}</span></div>
          <div class="vo-row"><span class="vo-key">Grand Total</span><span class="vo-val"><strong>₹{{ formatCurrency(payDetail.order.grand_total) }}</strong></span></div>
          <div class="vo-row">
            <span class="vo-key">Shipping Method</span>
            <span class="vo-val">{{ formatLabel(payDetail.order.shipping_method) }}</span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Payment Status</span>
            <span class="vo-val"><span :class="['ix-pill', paymentStatusPill(payDetail.order.payment_status)]">{{ formatLabel(payDetail.order.payment_status) }}</span></span>
          </div>
        </div>

        <!-- Payment screenshots — transfer proof; reviewers can add / remove -->
        <div class="vo-section vo-section--proof">
          <div class="rf-proof-head">
            <span class="rf-proof-title">Payment Screenshot{{ paymentScreenshots.length === 1 ? '' : 's' }}<template v-if="paymentScreenshots.length"> ({{ paymentScreenshots.length }})</template></span>
          </div>
          <div v-if="paymentScreenshots.length" class="pd-proof-grid">
            <div v-for="(shot, sIdx) in paymentScreenshots" :key="shot.key || shot.url || sIdx" class="pd-proof-cell">
              <button
                type="button"
                class="pd-proof-link"
                :title="shot.name || 'View full size'"
                @click="openGallery(sIdx)"
              >
                <img
                  v-if="isImageScreenshot(shot)"
                  :src="shot.url"
                  :alt="shot.name || `Payment screenshot ${sIdx + 1}`"
                  class="pd-proof-thumb"
                  loading="lazy"
                />
                <span v-else class="pd-proof-pdf">
                  <span class="pd-proof-pdf__badge">PDF</span>
                  <span class="pd-proof-pdf__name">{{ shot.name || 'Document' }}</span>
                </span>
                <span class="pd-proof-open">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    <line x1="11" y1="8" x2="11" y2="14"/>
                    <line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                </span>
              </button>
              <button
                v-if="canEditProof"
                type="button"
                class="pd-proof-remove"
                :aria-label="`Delete ${shot.name || 'screenshot'}`"
                :title="`Delete ${shot.name || 'screenshot'}`"
                :disabled="payDetail.proofBusy"
                @click.stop="askDeleteScreenshot(shot)"
              >×</button>
            </div>
          </div>
          <p v-else class="pd-proof-empty">No payment screenshot attached yet.</p>

          <div v-if="canEditProof" class="pd-proof-actions">
            <input
              ref="proofFileInput"
              type="file"
              class="pd-proof-file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              multiple
              @change="onProofFilesSelected"
            />
            <button
              type="button"
              class="ix-btn ix-btn--ghost pd-proof-add"
              :disabled="payDetail.proofUploading > 0 || payDetail.proofBusy"
              @click="triggerProofUpload"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              <span>{{ payDetail.proofUploading > 0 ? `Uploading ${payDetail.proofUploading}…` : 'Add screenshot' }}</span>
            </button>
          </div>
          <p v-if="payDetail.proofError" class="pd-proof-error">{{ payDetail.proofError }}</p>
        </div>

        <!-- Written payment-proof note (alternative to a screenshot) -->
        <div v-if="paymentProofNote" class="vo-section vo-section--proof">
          <div class="rf-proof-head">
            <span class="rf-proof-title">Payment Note</span>
          </div>
          <p class="pd-proof-note">{{ paymentProofNote }}</p>
        </div>

        <!-- Inline payment-method update -->
        <div class="vo-section ix-pd-method">
          <div class="vo-row vo-row--wide">
            <span class="vo-key">Payment Method</span>
            <span class="vo-val ix-pd-method__control">
              <AppDropdown
                v-model="payDetail.methodValue"
                :options="methodUpdateOptions"
                searchable
                search-placeholder="Search methods…"
              />
              <button
                type="button"
                class="ix-btn ix-btn--ghost ix-pd-method__save"
                :disabled="payDetail.savingMethod || !payDetail.methodValue || payDetail.methodValue === payDetail.order.payment_method"
                @click="submitDetailMethod"
              >{{ payDetail.savingMethod ? 'Saving…' : 'Update' }}</button>
            </span>
          </div>
          <div v-if="payDetail.methodError" class="ix-pd-method__error">{{ payDetail.methodError }}</div>
        </div>

        <!-- Payment date — required before the payment can be approved -->
        <div v-if="payDetail.order.payment_status !== 'payment_received'" class="vo-section ix-pd-date">
          <div class="vo-row vo-row--wide">
            <span class="vo-key">Payment Date <span class="ix-pd-req">*</span></span>
            <span class="vo-val ix-pd-date__control">
              <SingleDatePicker :value="payDetail.paymentDate" @input="payDetail.paymentDate = $event" />
            </span>
          </div>
          <p class="ix-pd-date__hint">Set the date the payment was received — required to approve.</p>
        </div>
      </div>

      <template #footer>
        <button type="button" class="ix-btn ix-btn--ghost" :disabled="payDetail.approving" @click="payDetail.show = false">Close</button>
        <button type="button" class="ix-btn ix-btn--ghost" :disabled="!payDetail.order || payDetail.approving" @click="editOrderFromDetail">Edit Order</button>
        <button
          type="button"
          class="ix-btn ix-btn--primary"
          :disabled="!payDetail.order || payDetail.approving || !payDetail.paymentDate || payDetail.order.payment_status === 'payment_received'"
          @click="approveFromDetail"
        >{{ payDetail.approving ? 'Approving…' : 'Approve Payment' }}</button>
      </template>
    </CModal>

    <!-- Payment-screenshot gallery — full-screen lightbox layered above the
         order-detail modal. Browse every attached proof without leaving the
         approval flow. -->
    <transition name="ix-gallery-fade">
      <div
        v-if="gallery.show"
        class="ix-gallery"
        role="dialog"
        aria-modal="true"
        aria-label="Payment screenshot viewer"
        @click.self="closeGallery"
      >
        <div class="ix-gallery__bar" @click.stop>
          <span class="ix-gallery__title">{{ galleryShot && (galleryShot.name || ('Screenshot ' + (gallery.index + 1))) }}</span>
          <span class="ix-gallery__count">{{ gallery.index + 1 }} / {{ paymentScreenshots.length }}</span>
          <a
            v-if="galleryShot"
            :href="galleryShot.url"
            target="_blank"
            rel="noopener"
            class="ix-gallery__action"
            title="Open original in new tab"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            <span>Open original</span>
          </a>
          <button type="button" class="ix-gallery__close" title="Close (Esc)" @click="closeGallery">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="ix-gallery__stage" @click.self="closeGallery">
          <button
            v-if="paymentScreenshots.length > 1"
            type="button"
            class="ix-gallery__nav ix-gallery__nav--prev"
            title="Previous (←)"
            @click.stop="galleryPrev"
          >
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          <div class="ix-gallery__viewport" @click.stop>
            <img
              v-if="galleryShot && isImageScreenshot(galleryShot)"
              :src="galleryShot.url"
              :alt="galleryShot.name || ('Screenshot ' + (gallery.index + 1))"
              class="ix-gallery__image"
            />
            <iframe
              v-else-if="galleryShot"
              :src="galleryShot.url"
              class="ix-gallery__frame"
              :title="galleryShot.name || 'Document'"
            ></iframe>
          </div>

          <button
            v-if="paymentScreenshots.length > 1"
            type="button"
            class="ix-gallery__nav ix-gallery__nav--next"
            title="Next (→)"
            @click.stop="galleryNext"
          >
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

        <div v-if="paymentScreenshots.length > 1" class="ix-gallery__strip" @click.stop>
          <button
            v-for="(shot, tIdx) in paymentScreenshots"
            :key="shot.key || tIdx"
            type="button"
            :class="['ix-gallery__thumb', { 'is-active': tIdx === gallery.index }]"
            :title="shot.name || ('Screenshot ' + (tIdx + 1))"
            @click="gallery.index = tIdx"
          >
            <img
              v-if="isImageScreenshot(shot)"
              :src="shot.url"
              :alt="shot.name || ('Screenshot ' + (tIdx + 1))"
              loading="lazy"
            />
            <span v-else class="ix-gallery__thumb-pdf">PDF</span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import { buildComplaintDraft, copyToClipboard, formatComplaintDate } from '@/utils/codComplaint'

function inboxIsoDate (offsetDays = 0) {
  const d = new Date()
  if (offsetDays) d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function inboxIsoToday () {
  return inboxIsoDate(0)
}

const TYPE_LABELS = {
  discount_approval: 'Discount approval',
  refund_approval: 'Refund approval',
  payment_overdue_reminder: 'Payment overdue',
  counter_mismatch_reminder: 'Counter mismatch'
}

// Types that carry an approve/reject decision on the backend. Anything
// else opens as a read-only FYI — auto-resolved when the underlying
// state clears (e.g. payment_overdue_reminder closes when the order
// is paid or cancelled).
const APPROVAL_TYPES = new Set(['discount_approval', 'refund_approval'])
const isApprovalType = (t) =>
  APPROVAL_TYPES.has(t) || (typeof t === 'string' && t.endsWith('_approval'))

// Sidebar categories. Each owns a predicate so we can absorb future
// inbox types (e.g. 'price_override_approval', 'follow_up_reminder')
// without touching the template. `discount_approval` is the only type
// the backend emits today; the placeholder categories show empty until
// their backing types ship.
const CATEGORIES = [
  {
    key: 'all',
    label: 'All Inbox',
    match: () => true,
    icon: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/></svg>'
  },
  {
    key: 'approval',
    label: 'Approvals',
    match: (t) => typeof t === 'string' && (t === 'discount_approval' || t.endsWith('_approval')),
    icon: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>'
  },
  {
    key: 'announcement',
    label: 'Announcements',
    match: (t) => typeof t === 'string' && (t === 'announcement' || t.startsWith('announcement')),
    icon: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 21 4 21 20 3 13 3 11"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>'
  },
  {
    key: 'reminder',
    label: 'Reminders',
    match: (t) => typeof t === 'string' && (t === 'reminder' || t.includes('reminder')),
    icon: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>'
  },
  // Order-backed view, not an inbox-item bucket. `match` never matches a
  // notification type — the main pane renders the payment-approvals table
  // instead of the item list when this category is active, and its badge
  // reflects the loaded pending-payment count rather than `items`.
  {
    key: 'payment',
    label: 'Payment Approvals',
    isPayment: true,
    match: () => false,
    icon: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>'
  }
]

const STATUSES = [
  { key: 'open',     label: 'Pending action' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all',      label: 'All' }
]

export default {
  name: 'Inbox',
  components: { AppConfirmDialog, AppDropdown, SingleDatePicker, BaseTable },
  data () {
    const today = inboxIsoToday()
    return {
      categories: CATEGORIES,
      statuses: STATUSES,
      activeCategory: 'all',
      activeStatus: 'open',
      items: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      showDetail: false,
      activeItem: null,
      resolveNote: '',
      isResolving: false,
      pendingResolution: null,
      // Full order record fetched when an order-backed inbox item is opened —
      // the inbox `subject` only carries a slim summary, so the Review modal
      // pulls the complete order (items, address, dates) on demand.
      orderDetail: {
        loading: false,
        error: '',
        order: null
      },
      // Post-office COD complaint sub-form, shown on a payment-overdue
      // reminder whose order is COD/VPP + still pending.
      complaint: {
        id: '',
        date: inboxIsoToday(),
        notes: '',
        by: '',
        filed: false,
        draft: '',
        saving: false,
        clearing: false,
        copied: false,
        error: ''
      },
      // Mobile-only — desktop layout shows the sidebar at all times.
      sidebarOpen: false,
      confirmDialog: {
        visible: false,
        title: '',
        message: '',
        confirmLabel: '',
        variant: 'warning',
        action: null
      },

      // ── Payment Approvals view ──────────────────────────────────────
      // Default to a 90-day window: unlike VPP/COD reconciliation (which is
      // same-day), non-COD payment approvals are a backlog that skews older,
      // so a today-only default would render empty. Users can widen From.
      payments: {
        fromDate: inboxIsoDate(-90),
        toDate: today,
        method: 'all',
        loading: false,
        loaded: false,
        results: [],
        methods: [],
        sum: 0,
        count: 0
      },
      paymentColumns: [
        { key: 'order_number', label: 'Order Number' },
        { key: 'order_date', label: 'Order Date', width: '120px' },
        { key: 'customer', label: 'Customer' },
        { key: 'payment_method', label: 'Method', width: '120px' },
        { key: 'grand_total', label: 'Amount', align: 'right', width: '130px' },
        { key: 'action', label: '', align: 'right', width: '180px' }
      ],
      // Single order-detail modal that consolidates review + approve +
      // update-method + edit. `row` is the slim table row; `order` is the
      // full record fetched on open.
      payDetail: {
        show: false,
        row: null,
        order: null,
        loading: false,
        error: '',
        approving: false,
        paymentDate: '',
        methodValue: '',
        savingMethod: false,
        methodError: '',
        // Payment-proof attach / delete state, scoped to the open order.
        proofUploading: 0,
        proofBusy: false,
        proofError: ''
      },
      // Confirm-before-delete state for a single payment screenshot.
      proofDelete: { visible: false, shot: null, message: '' },
      // Canonical payment-method options for the update dropdown — lazily
      // loaded from /api/order_form_data the first time it's needed.
      paymentMethodOptions: [],
      paymentMethodOptionsLoaded: false,
      // Full-screen screenshot viewer layered above the order-detail modal.
      gallery: {
        show: false,
        index: 0
      }
    }
  },
  computed: {
    activeCategoryDef () {
      return this.categories.find((c) => c.key === this.activeCategory) || this.categories[0]
    },
    activeStatusDef () {
      return this.statuses.find((s) => s.key === this.activeStatus) || this.statuses[0]
    },
    visibleItems () {
      const catMatch = this.activeCategoryDef.match
      return this.items.filter((item) => {
        if (!catMatch(item.type)) return false
        if (this.activeStatus === 'all') return true
        return item.status === this.activeStatus
      })
    },
    // Sidebar badges always count OPEN items per category, irrespective
    // of the currently-selected status filter. The badge answers "what
    // needs my attention here?", not "what am I currently looking at?".
    categoryCounts () {
      const counts = {}
      for (const cat of this.categories) {
        counts[cat.key] = this.items.filter((i) => i.status === 'open' && cat.match(i.type)).length
      }
      // Payment Approvals isn't backed by `items` — its badge is the
      // count of pending-payment orders loaded for the active filters.
      counts.payment = this.payments.count
      return counts
    },
    statusCounts () {
      const counts = { open: 0, resolved: 0, all: this.items.length }
      for (const item of this.items) {
        if (item.status === 'open') counts.open++
        else if (item.status === 'resolved') counts.resolved++
      }
      return counts
    },
    emptyMessage () {
      if (this.activeCategory === 'announcement') return 'No announcements yet. Broadcasts will appear here.'
      if (this.activeCategory === 'reminder') return 'No reminders right now. Scheduled follow-ups will land here.'
      if (this.activeStatus === 'resolved') return 'Nothing resolved yet for this category.'
      return 'Nothing here. New items will appear in this list.'
    },
    detailTitle () {
      if (!this.activeItem) return 'Inbox item'
      return this.activeItem.title || 'Inbox item'
    },
    // The order shown in the Review modal: the slim inbox `subject` enriched
    // with the full order record once it loads (items, address, dates, the
    // real discount %). Subject keys win for the few fields the order record
    // doesn't carry (e.g. item_count).
    detailOrder () {
      const it = this.activeItem
      if (!it || it.subject_type !== 'order' || !it.subject) return null
      const order = this.orderDetail.order
      if (!order) return it.subject
      // Order record is the source of truth; subject fills the few keys it
      // lacks (e.g. item_count), then order's richer values overlay back.
      return { ...it.subject, ...order }
    },
    detailOrderItems () {
      const order = this.orderDetail.order
      return (order && Array.isArray(order.order_items)) ? order.order_items : []
    },
    // FYI reminders (e.g. payment_overdue_reminder) have no approve /
    // reject side-effect — they auto-resolve when the underlying state
    // clears. Only approval types surface the action buttons + note.
    isActionable () {
      return !!this.activeItem
        && this.activeItem.status === 'open'
        && isApprovalType(this.activeItem.type)
        && !this.isSubjectCancelled(this.activeItem)
    },
    // The post-office complaint sub-form only makes sense on an overdue
    // payment reminder whose order is a still-pending COD/VPP parcel.
    showInboxComplaint () {
      const it = this.activeItem
      if (!it || it.subject_type !== 'order' || !it.subject) return false
      if (it.type !== 'payment_overdue_reminder') return false
      const method = String(it.subject.payment_method || '').toLowerCase()
      const status = String(it.subject.payment_status || '').toLowerCase()
      return (method === 'vpp' || method === 'cod') && status === 'payment_pending'
    },
    isPaymentView () {
      return this.activeCategory === 'payment'
    },
    // True while the active view (inbox list vs payment table) is fetching.
    headerLoading () {
      return this.isPaymentView ? this.payments.loading : this.isLoading
    },
    // Filter dropdown: "All methods" + every distinct method present in the
    // current pending-payment set (server-provided so it stays stable).
    paymentMethodFilterOptions () {
      const opts = [{ value: 'all', label: 'All methods' }]
      for (const m of this.payments.methods) {
        if (m) opts.push({ value: m, label: this.formatLabel(m) })
      }
      return opts
    },
    // Options for the update-method modal — canonical list from the order
    // form when available, otherwise the distinct methods we already have.
    methodUpdateOptions () {
      if (this.paymentMethodOptions.length) return this.paymentMethodOptions
      return this.payments.methods.filter(Boolean).map((m) => ({ value: m, label: this.formatLabel(m) }))
    },
    // Bank/UPI transfer proof attached at order entry (TeleSales / OrderForm).
    // Cast to array on the backend, but guard against a legacy JSON string or
    // null. Prefer the full order (from /api/orders/{id}); fall back to the
    // queue row, which also carries the list.
    paymentScreenshots () {
      const raw = (this.payDetail.order && this.payDetail.order.payment_screenshots) ||
        (this.payDetail.row && this.payDetail.row.payment_screenshots) || []
      if (Array.isArray(raw)) return raw.filter((s) => s && s.url)
      if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw)
          return Array.isArray(parsed) ? parsed.filter((s) => s && s.url) : []
        } catch (e) {
          return []
        }
      }
      return []
    },
    // The screenshot currently shown in the gallery lightbox.
    galleryShot () {
      return this.paymentScreenshots[this.gallery.index] || null
    },
    // Written proof note (UPI ref / "paid at counter" / etc.) — the staff's
    // alternative when the customer didn't send a screenshot. Same source
    // precedence as paymentScreenshots: full order first, queue row fallback.
    paymentProofNote () {
      const note = (this.payDetail.order && this.payDetail.order.payment_proof_note) ||
        (this.payDetail.row && this.payDetail.row.payment_proof_note) || ''
      return typeof note === 'string' ? note.trim() : ''
    },
    // Who may add / delete payment proof — mirrors the backend gate on
    // appendPaymentScreenshots / deletePaymentScreenshot. Needs a loaded order
    // (its id drives both endpoints).
    canEditProof () {
      if (!this.payDetail.order || !this.payDetail.order.id) return false
      const allowed = ['superadmin', 'admin', 'manager', 'accountant', 'telesales', 'biller', 'field_staff']
      const roles = this.getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.some((r) => allowed.includes(r))
    }
  },
  created () {
    this.loadItems()
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onGalleryKey)
  },
  watch: {
    activeCategory (next) {
      // Lazy-load the payment table the first time the tab is opened so the
      // inbox itself isn't slowed by an extra request on every page mount.
      if (next === 'payment' && !this.payments.loaded && !this.payments.loading) {
        this.loadPaymentApprovals()
      }
    },
    // If the order-detail modal closes while the gallery is open, tear the
    // lightbox down too so it doesn't linger over the dimmed background.
    'payDetail.show' (open) {
      if (!open && this.gallery.show) this.closeGallery()
    }
  },
  methods: {
    apiBase () {
      return this.$apiAdress
    },
    loadItems () {
      // Single fetch returns every status — sidebar badge counts and
      // category-bucketed lists are both computed client-side off the
      // same payload. Backend caps at 500 rows; inbox volume is far
      // below that today.
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.apiBase()}/api/inbox`, { params: { status: 'all' } })
        .then((response) => {
          this.items = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load inbox.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    // Refresh button routes to whichever view is active.
    onRefresh () {
      if (this.isPaymentView) this.loadPaymentApprovals()
      else this.loadItems()
    },

    // ── Payment Approvals ─────────────────────────────────────────────
    formatINR (value) {
      const n = Number(value)
      if (Number.isNaN(n)) return '—'
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n)
    },
    methodPillClass (value) {
      const s = String(value || '').toLowerCase()
      if (s === 'vpp' || s === 'cod') return 'ix-pill--amber'
      if (s === 'adjustment') return 'ix-pill--gray'
      return 'ix-pill--blue'
    },
    loadPaymentApprovals () {
      if (!this.payments.fromDate || !this.payments.toDate) {
        this.errorMessage = 'From Date and To Date are required.'
        return
      }
      this.errorMessage = ''
      this.successMessage = ''
      this.payments.loading = true
      // All-org approvals queue — the backend pools every organisation's
      // pending payments for switch-org users, so no org id is sent.
      axios.post(`${this.apiBase()}/api/payment_approvals`, {
        fromDate: this.payments.fromDate,
        toDate: this.payments.toDate,
        payment_method: this.payments.method
      })
        .then((response) => {
          const data = response.data || {}
          this.payments.results = Array.isArray(data.orders) ? data.orders : []
          this.payments.methods = Array.isArray(data.methods) ? data.methods : []
          this.payments.sum = Number(data.sum || 0)
          this.payments.count = Number(data.count || this.payments.results.length)
          this.payments.loaded = true
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load pending payments.'
          this.payments.results = []
          this.payments.sum = 0
          this.payments.count = 0
        })
        .finally(() => { this.payments.loading = false })
    },
    resetPaymentFilters () {
      this.payments.fromDate = inboxIsoDate(-90)
      this.payments.toDate = inboxIsoToday()
      this.payments.method = 'all'
      this.loadPaymentApprovals()
    },
    // Open the consolidated detail modal: fetch the full order, seed the
    // method dropdown, and let the reviewer approve / update method / edit.
    openPaymentDetail (row) {
      this.payDetail = {
        show: true,
        row,
        order: null,
        loading: true,
        error: '',
        approving: false,
        // Empty by design — the approver must enter the actual receipt date
        // before Approve enables (the date is sent in the mark_paid payload).
        paymentDate: '',
        methodValue: row.payment_method || '',
        savingMethod: false,
        methodError: '',
        proofUploading: 0,
        proofBusy: false,
        proofError: ''
      }
      this.proofDelete = { visible: false, shot: null, message: '' }
      this.loadPaymentMethodOptions()
      axios.get(`${this.apiBase()}/api/orders/${row.id}`)
        .then((response) => {
          const data = response.data
          let order = null
          if (data && typeof data === 'object' && data.id) order = data
          else if (Array.isArray(data)) order = data[0] || null
          else if (data && Array.isArray(data.data)) order = data.data[0] || null
          if (!order) { this.payDetail.error = 'Order not found.'; return }
          this.payDetail.order = order
          this.payDetail.methodValue = order.payment_method || row.payment_method || ''
        })
        .catch((error) => {
          this.payDetail.error = error?.response?.data?.message || 'Unable to load order details.'
        })
        .finally(() => { this.payDetail.loading = false })
    },
    // Approve from inside the detail modal — mark received (today's date).
    approveFromDetail () {
      const order = this.payDetail.order
      if (!order || !order.order_number) return
      if (!this.payDetail.paymentDate) {
        this.payDetail.error = 'Enter the payment date before approving.'
        return
      }
      this.payDetail.approving = true
      this.payDetail.error = ''
      this.errorMessage = ''
      this.successMessage = ''
      axios.post(`${this.apiBase()}/api/orders/mark_paid`, {
        order_id: order.order_number,
        field: 'order_number',
        amount: Number(order.grand_total || 0),
        comments: 'Approved from Inbox — Payment Approvals',
        payment_date: this.payDetail.paymentDate,
        type: 'update'
      })
        .then(() => {
          this.successMessage = `Payment approved for ${order.order_number}.`
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.removePaymentRow(order.order_number)
          this.payDetail.show = false
          this.payDetail.order = null
        })
        .catch((error) => {
          this.payDetail.error = error?.response?.data?.message || 'Unable to approve payment.'
        })
        .finally(() => { this.payDetail.approving = false })
    },
    // Update payment method inline (quick_update), patching row + modal.
    submitDetailMethod () {
      const order = this.payDetail.order
      const method = this.payDetail.methodValue
      if (!order || !method) { this.payDetail.methodError = 'Select a payment method.'; return }
      if (method === order.payment_method) { this.payDetail.methodError = 'This is already the order’s payment method.'; return }
      this.payDetail.savingMethod = true
      this.payDetail.methodError = ''
      axios.patch(`${this.apiBase()}/api/orders/${order.id}/quick_update`, { payment_method: method })
        .then(() => {
          this.successMessage = `Payment method updated for ${order.order_number}.`
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.payDetail.order = { ...order, payment_method: method }
          const target = this.payments.results.find((r) => r.id === order.id)
          if (target) target.payment_method = method
        })
        .catch((error) => {
          this.payDetail.methodError = error?.response?.data?.message
            || (error?.response?.status === 403 ? 'You are not allowed to edit orders.' : 'Unable to update payment method.')
        })
        .finally(() => { this.payDetail.savingMethod = false })
    },
    // Jump to the full order edit form (OrderForm in edit mode).
    editOrderFromDetail () {
      const order = this.payDetail.order
      if (!order) return
      this.payDetail.show = false
      this.$router.push({
        path: '/orderform',
        query: { order_id: order.id, order_number: order.order_number || '' }
      }).catch(() => {})
    },
    orderItemTotal (item) {
      const price = Number(item && item.price || 0)
      const qty = Number(item && item.quantity || 0)
      const total = price * qty
      return Number.isFinite(total) ? total : '—'
    },
    // `discount_percentage` is only persisted when the discount was entered
    // as a percentage; flat-amount discounts leave it null. Fall back to the
    // effective rate (discount ÷ subtotal) so the figure always shows.
    discountPercentDisplay (order) {
      if (!order) return '—'
      const pct = Number(order.discount_percentage)
      if (Number.isFinite(pct) && pct > 0) return `${pct}%`
      const discount = Number(order.discount)
      const subtotal = Number(order.total)
      if (Number.isFinite(discount) && discount > 0 && Number.isFinite(subtotal) && subtotal > 0) {
        const eff = (discount / subtotal) * 100
        return `${Number(eff.toFixed(2))}%`
      }
      return '—'
    },
    paymentStatusPill (value) {
      const s = String(value || '').toLowerCase()
      if (s === 'payment_received' || s === 'paid') return 'ix-pill--green'
      if (s === 'payment_pending') return 'ix-pill--amber'
      return 'ix-pill--gray'
    },
    loadPaymentMethodOptions () {
      if (this.paymentMethodOptionsLoaded) return
      const params = {}
      const orgId = Number(this.$store && this.$store.state && this.$store.state.activeOrganisationId)
      if (orgId) params.organisation_id = orgId
      axios.get(`${this.apiBase()}/api/order_form_data`, { params, timeout: 15000 })
        .then((response) => {
          const dropdown = (response.data && response.data.drop_down_items) || {}
          const entries = dropdown.payment_method || []
          this.paymentMethodOptions = entries
            .map((entry) => ({
              value: String(entry.slug || entry.value || entry.id || ''),
              label: entry.name || entry.text || entry.slug || ''
            }))
            .filter((entry) => entry.value)
          this.paymentMethodOptionsLoaded = true
        })
        .catch(() => {
          // Fall back to the distinct methods already present (methodUpdateOptions).
        })
    },
    // Drop a row from the table after it's been approved (no longer pending).
    removePaymentRow (orderNumber) {
      this.payments.results = this.payments.results.filter((r) => r.order_number !== orderNumber)
      this.payments.count = this.payments.results.length
      this.payments.sum = this.payments.results.reduce((s, r) => s + Number(r.grand_total || 0), 0)
    },

    openDetail (row) {
      this.activeItem = row
      this.resolveNote = ''
      this.pendingResolution = null
      this.seedComplaint(row)
      this.orderDetail = { loading: false, error: '', order: null }
      this.showDetail = true
      // Skip the full-order fetch for a cancelled (soft-deleted) order — the
      // resource `show` endpoint scopes those out and would surface an
      // "unavailable" error. The slim subject already carries the key fields.
      if (row && row.subject_type === 'order' && row.subject && row.subject.id && !this.isSubjectCancelled(row)) {
        this.loadOrderDetail(row.subject.id)
      }
    },
    // Pull the full order behind an order-backed inbox item so the Review
    // modal can show its complete details (items, address, dates) rather
    // than just the slim notification subject.
    loadOrderDetail (orderId) {
      this.orderDetail.loading = true
      this.orderDetail.error = ''
      this.orderDetail.order = null
      axios.get(`${this.apiBase()}/api/orders/${orderId}`)
        .then((response) => {
          const data = response.data
          let order = null
          if (data && typeof data === 'object' && data.id) order = data
          else if (Array.isArray(data)) order = data[0] || null
          else if (data && Array.isArray(data.data)) order = data.data[0] || null
          if (!order) { this.orderDetail.error = 'Order details unavailable.'; return }
          this.orderDetail.order = order
        })
        .catch((error) => {
          this.orderDetail.error = error?.response?.data?.message || 'Unable to load full order details.'
        })
        .finally(() => { this.orderDetail.loading = false })
    },
    // ── Post Office COD complaint ──────────────────────────────────────
    seedComplaint (item) {
      const c = this.complaint
      c.error = ''
      c.copied = false
      c.saving = false
      c.clearing = false
      const subject = (item && item.subject_type === 'order') ? item.subject : null
      if (!subject) {
        c.id = ''; c.date = inboxIsoToday(); c.notes = ''; c.by = ''; c.filed = false; c.draft = ''
        return
      }
      c.id = subject.cod_complaint_id || ''
      c.date = subject.cod_complaint_date || inboxIsoToday()
      c.notes = subject.cod_complaint_notes || ''
      c.by = (subject.complaint_by && subject.complaint_by.name) || ''
      c.filed = !!subject.cod_complaint_id
      c.draft = buildComplaintDraft(subject)
    },
    complaintDateDisplay (value) {
      return formatComplaintDate(value)
    },
    async copyInboxComplaint () {
      const ok = await copyToClipboard(this.complaint.draft || '')
      if (ok) {
        this.complaint.copied = true
        setTimeout(() => { this.complaint.copied = false }, 2000)
      } else {
        this.complaint.error = 'Could not copy — select the text and copy manually.'
      }
    },
    saveInboxComplaint () {
      const it = this.activeItem
      const orderNumber = it && it.subject && it.subject.order_number
      const id = (this.complaint.id || '').trim()
      if (!orderNumber) return
      if (!id) {
        this.complaint.error = 'Enter the complaint ID from the post office.'
        return
      }
      this.complaint.error = ''
      this.complaint.saving = true
      axios.post(`${this.apiBase()}/api/cod_complaint`, {
        order_number: orderNumber,
        complaint_id: id,
        complaint_date: this.complaint.date || inboxIsoToday(),
        complaint_notes: this.complaint.notes || null
      })
        .then((response) => {
          const d = response.data || {}
          this.successMessage = `Complaint ${d.cod_complaint_id || id} recorded for ${orderNumber}.`
          this.complaint.id = d.cod_complaint_id || id
          this.complaint.date = d.cod_complaint_date || this.complaint.date
          this.complaint.by = d.cod_complaint_by || this.complaint.by
          this.complaint.notes = d.cod_complaint_notes || ''
          this.complaint.filed = true
          // Reflect onto the in-memory subject so re-opening the item shows
          // the filed state without a full inbox reload.
          if (it.subject) {
            it.subject.cod_complaint_id = this.complaint.id
            it.subject.cod_complaint_date = this.complaint.date
            it.subject.cod_complaint_notes = this.complaint.notes
            it.subject.complaint_by = { name: this.complaint.by }
          }
        })
        .catch((error) => {
          this.complaint.error = error?.response?.data?.message || 'Unable to record complaint.'
        })
        .finally(() => { this.complaint.saving = false })
    },
    clearInboxComplaint () {
      const it = this.activeItem
      const orderNumber = it && it.subject && it.subject.order_number
      if (!orderNumber) return
      this.complaint.error = ''
      this.complaint.clearing = true
      axios.post(`${this.apiBase()}/api/cod_complaint/clear`, { order_number: orderNumber })
        .then(() => {
          this.successMessage = `Complaint cleared for ${orderNumber}.`
          this.complaint.id = ''
          this.complaint.by = ''
          this.complaint.notes = ''
          this.complaint.date = inboxIsoToday()
          this.complaint.filed = false
          if (it.subject) {
            it.subject.cod_complaint_id = null
            it.subject.cod_complaint_date = null
            it.subject.cod_complaint_notes = null
            it.subject.complaint_by = null
          }
        })
        .catch((error) => {
          this.complaint.error = error?.response?.data?.message || 'Unable to clear complaint.'
        })
        .finally(() => { this.complaint.clearing = false })
    },
    confirmApprove () {
      const type = this.activeItem && this.activeItem.type
      let message = 'This action will resolve the inbox item.'
      if (type === 'discount_approval') {
        message = 'The order will be released into the confirmed flow and the customer notification will fire.'
      } else if (type === 'refund_approval') {
        message = 'The refund will be marked as completed. Disburse the amount manually via your bank — this only updates the OMS record.'
      }
      this.confirmDialog = {
        visible: true,
        title: 'Approve this request?',
        message,
        confirmLabel: 'Approve',
        variant: 'success',
        action: 'approve'
      }
      this.pendingResolution = 'approve'
    },
    confirmReject () {
      const type = this.activeItem && this.activeItem.type
      let message = 'This action will resolve the inbox item.'
      if (type === 'discount_approval') {
        message = 'The order will be cancelled and the customer will not be notified. This can be reversed only by re-creating the order.'
      } else if (type === 'refund_approval') {
        message = 'The refund request will be marked as rejected. The staff member who raised it will need to submit a fresh request if this was rejected by mistake.'
      }
      this.confirmDialog = {
        visible: true,
        title: 'Reject this request?',
        message,
        confirmLabel: 'Reject',
        variant: 'danger',
        action: 'reject'
      }
      this.pendingResolution = 'reject'
    },
    runResolve () {
      if (!this.activeItem || !this.confirmDialog.action) return
      const action = this.confirmDialog.action
      const url = `${this.apiBase()}/api/inbox/${this.activeItem.id}/${action}`
      this.isResolving = true
      axios.post(url, { note: this.resolveNote || null })
        .then((response) => {
          this.successMessage = action === 'approve' ? 'Request approved.' : 'Request rejected.'
          setTimeout(() => { this.successMessage = '' }, 2500)
          this.activeItem = response.data
          this.loadItems()
          this.confirmDialog.visible = false
          this.showDetail = false
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Failed to resolve item.'
          this.confirmDialog.visible = false
        })
        .finally(() => {
          this.isResolving = false
          this.pendingResolution = null
        })
    },
    formatType (type) {
      return TYPE_LABELS[type] || this.formatLabel(type)
    },
    typePillClass (type) {
      if (type === 'discount_approval') return 'ix-pill--amber'
      if (type === 'refund_approval') return 'ix-pill--amber'
      if (typeof type === 'string' && type.includes('announcement')) return 'ix-pill--blue'
      if (typeof type === 'string' && type.includes('reminder')) return 'ix-pill--gray'
      return 'ix-pill--blue'
    },
    refundOrderNumber (item) {
      if (!item) return '—'
      const payload = item.payload || {}
      if (payload.order_number) return payload.order_number
      const subject = item.subject || {}
      if (subject.order && subject.order.order_number) return subject.order.order_number
      if (payload.order_id) return `#${payload.order_id}`
      if (subject.order_id) return `#${subject.order_id}`
      return '—'
    },
    refundRequestedBy (item) {
      if (!item) return '—'
      const payload = item.payload || {}
      return payload.requested_by_name || (payload.requested_by_user_id ? `User #${payload.requested_by_user_id}` : '—')
    },
    // Payment-screenshot thumbnails render as <img> unless the file is a PDF
    // (then a badge). Sniff the mime first, fall back to the URL/name extension.
    isImageScreenshot (shot) {
      if (!shot) return false
      if (typeof shot.mime === 'string') return shot.mime.startsWith('image/')
      const stripped = String(shot.url || shot.name || '').split('?')[0].split('#')[0]
      const ext = stripped.slice(stripped.lastIndexOf('.') + 1).toLowerCase()
      return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)
    },
    // ── Payment-proof attach / delete ─────────────────────────────────
    // Roles are stashed in localStorage at login (same source Orders.vue reads).
    getStoredRoles () {
      const raw = localStorage.getItem('roles')
      if (!raw) return []
      return raw.split(',').map((r) => r.trim()).filter(Boolean)
    },
    triggerProofUpload () {
      this.payDetail.proofError = ''
      const input = this.$refs.proofFileInput
      if (input) input.click()
    },
    async onProofFilesSelected (event) {
      const input = event.target
      const files = Array.from(input.files || [])
      input.value = ''
      if (!files.length) return

      this.payDetail.proofError = ''
      const uploaded = []
      for (const file of files) {
        const meta = await this.uploadProofFile(file)
        if (meta) uploaded.push(meta)
      }
      if (uploaded.length) await this.appendProofScreenshots(uploaded)
    },
    async uploadProofFile (file) {
      // Mirror PaymentScreenshotController's validation: images + PDF, max 10MB.
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
      if (!allowed.includes(file.type)) {
        this.payDetail.proofError = `${file.name}: unsupported type. Upload an image or PDF.`
        return null
      }
      if (file.size > 10 * 1024 * 1024) {
        this.payDetail.proofError = `${file.name}: exceeds the 10MB limit.`
        return null
      }

      this.payDetail.proofUploading += 1
      try {
        const fd = new FormData()
        fd.append('file', file)
        const { data } = await axios.post(
          `${this.apiBase()}/api/payment-screenshots/upload`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        )
        return {
          url: data.url,
          key: data.key,
          name: data.name || file.name,
          mime: data.mime || file.type,
          size: data.size || file.size
        }
      } catch (error) {
        this.payDetail.proofError = error?.response?.data?.error
          || error?.response?.data?.message
          || `${file.name}: upload failed. Please try again.`
        return null
      } finally {
        this.payDetail.proofUploading -= 1
      }
    },
    // Persist the freshly-uploaded files onto the order (backend merges).
    async appendProofScreenshots (screenshots) {
      const order = this.payDetail.order
      if (!order || !order.id || !screenshots.length) return
      this.payDetail.proofBusy = true
      this.payDetail.proofError = ''
      try {
        const { data } = await axios.post(
          `${this.apiBase()}/api/orders/${order.id}/payment_proof`,
          { payment_screenshots: screenshots.map((f) => ({ url: f.url, key: f.key, name: f.name, mime: f.mime, size: f.size })) }
        )
        this.applyProofUpdate(data)
      } catch (error) {
        this.payDetail.proofError = error?.response?.data?.message
          || (error?.response?.status === 403 ? 'You are not allowed to edit payment proof.' : 'Failed to save screenshot.')
      } finally {
        this.payDetail.proofBusy = false
      }
    },
    askDeleteScreenshot (shot) {
      if (!shot) return
      this.proofDelete = {
        visible: true,
        shot,
        message: `Remove ${shot.name ? `“${shot.name}”` : 'this screenshot'} from the order? This can’t be undone.`
      }
    },
    async confirmDeleteScreenshot () {
      const order = this.payDetail.order
      const shot = this.proofDelete.shot
      if (!order || !order.id || !shot) { this.proofDelete.visible = false; return }
      this.payDetail.proofBusy = true
      this.payDetail.proofError = ''
      try {
        const { data } = await axios.delete(
          `${this.apiBase()}/api/orders/${order.id}/payment_proof`,
          { data: { key: shot.key || null, url: shot.url || null } }
        )
        this.applyProofUpdate(data)
        // Keep the lightbox coherent if it was open on a now-removed slide.
        if (this.gallery.show) {
          const n = this.paymentScreenshots.length
          if (!n) this.closeGallery()
          else if (this.gallery.index >= n) this.gallery.index = n - 1
        }
        this.proofDelete = { visible: false, shot: null, message: '' }
      } catch (error) {
        this.payDetail.proofError = error?.response?.data?.message
          || (error?.response?.status === 403 ? 'You are not allowed to edit payment proof.' : 'Failed to delete screenshot.')
        this.proofDelete.visible = false
      } finally {
        this.payDetail.proofBusy = false
      }
    },
    // Commit the order returned by an append / delete onto the modal + the
    // pending-payment row so the gallery reflects the change without a reload.
    applyProofUpdate (order) {
      if (!order || typeof order !== 'object') return
      const shots = Array.isArray(order.payment_screenshots) ? order.payment_screenshots : []
      this.payDetail.order = { ...this.payDetail.order, payment_screenshots: shots }
      const target = this.payments.results.find((r) => r.id === order.id)
      if (target) this.$set(target, 'payment_screenshots', shots)
    },
    // ── Screenshot gallery ────────────────────────────────────────────
    openGallery (index) {
      if (!this.paymentScreenshots.length) return
      this.gallery.index = Math.min(Math.max(index || 0, 0), this.paymentScreenshots.length - 1)
      this.gallery.show = true
      window.addEventListener('keydown', this.onGalleryKey)
    },
    closeGallery () {
      this.gallery.show = false
      window.removeEventListener('keydown', this.onGalleryKey)
    },
    galleryPrev () {
      const n = this.paymentScreenshots.length
      if (n < 2) return
      this.gallery.index = (this.gallery.index - 1 + n) % n
    },
    galleryNext () {
      const n = this.paymentScreenshots.length
      if (n < 2) return
      this.gallery.index = (this.gallery.index + 1) % n
    },
    onGalleryKey (e) {
      if (!this.gallery.show) return
      if (e.key === 'Escape') this.closeGallery()
      else if (e.key === 'ArrowLeft') this.galleryPrev()
      else if (e.key === 'ArrowRight') this.galleryNext()
    },
    refundProofUrl (item) {
      if (!item) return ''
      const subject = item.subject || {}
      const payload = item.payload || {}
      const fromImage = subject.imageFile || payload.imageFile
      if (fromImage && /^https?:\/\//i.test(fromImage)) return fromImage
      const proof = subject.payment_proof || fromImage
      if (!proof) return ''
      if (/^https?:\/\//i.test(proof)) return proof
      return `${this.$apiAdress}/${String(proof).replace(/^\/+/, '')}`
    },
    proofExtension (item) {
      const url = this.refundProofUrl(item)
      if (!url) return ''
      const stripped = String(url).split('?')[0].split('#')[0]
      const dot = stripped.lastIndexOf('.')
      if (dot === -1) return ''
      return stripped.slice(dot + 1).toLowerCase()
    },
    isRefundProofImage (item) {
      return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(this.proofExtension(item))
    },
    isRefundProofPdf (item) {
      return this.proofExtension(item) === 'pdf'
    },
    // A cancelled order is soft-deleted (deleted_at set). Two things set it:
    // the Orders page Cancel button, and rejecting a discount approval —
    // rejection cancels the order outright (InboxController::
    // resolveDiscountApproval), so it soft-deletes too.
    isSubjectCancelled (item) {
      return !!(item && item.subject_type === 'order' && item.subject && item.subject.deleted_at)
    },
    // Cancelled while this request was still UNDECIDED — the order died out
    // from under it, so there is nothing left to approve and the item reads
    // as "Cancelled" instead of its own status. A resolved item must never
    // match: rejecting is itself what cancels the order, so treating that as
    // "Cancelled" would erase the approved/rejected audit trail from the list.
    isSubjectCancelledUndecided (item) {
      return this.isSubjectCancelled(item) && !!item && item.status !== 'resolved'
    },
    // Template-accessible wrapper around the module-level isApprovalType.
    isApprovalItem (item) {
      return !!item && isApprovalType(item.type)
    },
    // Item-aware wrappers used by the templates: an order cancelled out from
    // under an undecided item wins over that item's own status; a resolved
    // item always shows its own outcome.
    displayStatusLabel (item) {
      if (this.isSubjectCancelledUndecided(item)) return 'Cancelled'
      return this.formatStatus(item.status, item.resolution)
    },
    displayStatusClass (item) {
      if (this.isSubjectCancelledUndecided(item)) return 'ix-pill--gray'
      return this.statusPillClass(item.status, item.resolution)
    },
    formatStatus (status, resolution) {
      if (status === 'resolved' && resolution) {
        return resolution === 'approved' ? 'Approved' : 'Rejected'
      }
      if (status === 'open') return 'Pending'
      if (status === 'dismissed') return 'Dismissed'
      return this.formatLabel(status)
    },
    statusPillClass (status, resolution) {
      if (status === 'open') return 'ix-pill--amber'
      if (status === 'resolved' && resolution === 'approved') return 'ix-pill--green'
      if (status === 'resolved' && resolution === 'rejected') return 'ix-pill--red'
      return 'ix-pill--gray'
    },
    rowActionLabel (row) {
      if (row.status !== 'open') return 'View'
      return isApprovalType(row.type) ? 'Review' : 'View'
    },
    barClass (row) {
      if (row.status === 'open') return 'ix-item__bar--amber'
      if (row.status === 'resolved' && row.resolution === 'approved') return 'ix-item__bar--green'
      if (row.status === 'resolved' && row.resolution === 'rejected') return 'ix-item__bar--red'
      return 'ix-item__bar--gray'
    },
    formatLabel (value) {
      if (!value) return '—'
      const str = String(value).replace(/_/g, ' ')
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    formatCurrency (value) {
      const num = Number(value || 0)
      return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDateTime (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },
    // Order Date for the payment-detail modal. Prefer `order_date` (a proper
    // datetime). `created_at` is cast to `timestamp` on the model, so it
    // serialises as Unix *seconds* — feeding that straight to `new Date()`
    // reads it as milliseconds and renders 1970, so convert it to ms first.
    orderDateDisplay (order) {
      if (!order) return '—'
      if (order.order_date) return this.formatDateTime(order.order_date)
      const ts = Number(order.created_at)
      if (Number.isFinite(ts) && ts > 0) return this.formatDateTime(ts * 1000)
      return this.formatDateTime(order.created_at)
    },
    formatRelative (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      const now = new Date()
      const diffMs = now - d
      const diffMin = Math.floor(diffMs / 60000)
      if (diffMin < 1) return 'just now'
      if (diffMin < 60) return `${diffMin}m ago`
      const diffHr = Math.floor(diffMin / 60)
      if (diffHr < 24) return `${diffHr}h ago`
      const diffDays = Math.floor(diffHr / 24)
      if (diffDays < 7) return `${diffDays}d ago`
      const sameYear = d.getFullYear() === now.getFullYear()
      if (sameYear) {
        return d.toLocaleString('en-IN', { day: '2-digit', month: 'short' })
      }
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
    }
  }
}
</script>

<style scoped>
/* Break the page out of the c-body main + CContainer fluid padding so
   the sidebar can run flush against the chrome — no gap above, below,
   or to the left of it. CHeader nav + breadcrumb subheader is ~92px;
   that's the only chrome above this view. */
.ix-page {
  margin: -1rem -15px 0;
}

/* ── Two-pane shell ──────────────────────────────────────────────── */
.ix-shell {
  display: grid;
  grid-template-columns: 240px 1fr;
  align-items: stretch;
  min-height: calc(100vh - 92px);
}

/* ── Sidebar (light, matches OMS section-card design system) ─────── */
.ix-sidebar {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e5e9f2;
  box-shadow: 1px 0 0 rgba(15, 23, 42, 0.02);
  overflow: hidden;
  position: sticky;
  top: 0;
  height: calc(100vh - 92px);
}

.ix-sidebar__head {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1.85rem 1.25rem 1.25rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.ix-sidebar__accent {
  width: 3px;
  align-self: stretch;
  border-radius: 3px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.ix-sidebar__head-text {
  flex: 1 1 auto;
  min-width: 0;
}

.ix-sidebar__head-text h1 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.ix-sidebar__head-text p {
  margin: 0.1rem 0 0;
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
}

.ix-sidebar__toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
}

.ix-nav {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.55rem 0.45rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.ix-nav__group {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.ix-nav__group-label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  padding: 0.3rem 0.55rem 0.2rem;
}

.ix-nav__item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  padding: 0.45rem 0.55rem;
  border: 0;
  background: transparent;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 500;
  font-family: inherit;
  text-align: left;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.14s ease, color 0.14s ease;
  position: relative;
  outline: none;
}

.ix-nav__item:focus,
.ix-nav__item:focus-visible {
  outline: none;
  box-shadow: none;
}

.ix-nav__item:hover:not(.is-active) {
  background: #f5fbf8;
  color: #0e5b46;
}

.ix-nav__item.is-active {
  background: #f0fbf6;
  color: #0e5b46;
  font-weight: 600;
}

.ix-nav__item.is-active::before {
  content: '';
  position: absolute;
  left: -0.05rem;
  top: 0.45rem;
  bottom: 0.45rem;
  width: 3px;
  border-radius: 3px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.ix-nav__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  flex-shrink: 0;
  transition: background 0.14s ease, color 0.14s ease;
}

.ix-nav__item:hover:not(.is-active) .ix-nav__icon,
.ix-nav__item.is-active .ix-nav__icon {
  background: rgba(22, 160, 122, 0.15);
  color: #0e5b46;
}

.ix-nav__icon >>> svg {
  display: block;
}

.ix-nav__label {
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ix-nav__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.66rem;
  font-weight: 700;
  flex-shrink: 0;
}

.ix-nav__count--muted {
  background: #f1f5f9;
  color: #64748b;
}

.ix-nav__item.is-active .ix-nav__count--muted {
  background: rgba(14, 91, 70, 0.12);
  color: #0e5b46;
}

.ix-nav__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: 0.35rem;
  margin-right: 0.1rem;
}

.ix-nav__dot--open     { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18); }
.ix-nav__dot--resolved { background: #16a07a; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18); }
.ix-nav__dot--all      { background: #94a3b8; box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.22); }

.ix-refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}

.ix-refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.ix-refresh-btn:focus-visible {
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.ix-refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.ix-refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.ix-refresh-btn:hover:not(:disabled) .ix-refresh-btn__icon {
  transform: rotate(60deg);
}

.ix-refresh-btn.is-loading .ix-refresh-btn__icon {
  animation: ix-spin 0.9s linear infinite;
  color: #16a07a;
}

.ix-sidebar__backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 998;
}

/* ── Main pane ───────────────────────────────────────────────────── */
.ix-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  /* Single white canvas — the list rows sit directly on it (Slack
     channel feel) instead of being wrapped in a separate card. The
     sidebar's right border now butts up against this surface with no
     visible page-background gap. */
  background: #ffffff;
}

.ix-main__head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1.85rem 1.25rem 1.25rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.ix-main__hamburger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  cursor: pointer;
}

.ix-main__title {
  flex: 1 1 auto;
  min-width: 0;
}

.ix-main__title h2 {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.ix-main__title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  background: #f0fbf6;
  color: #0e5b46;
}

.ix-main__title-icon >>> svg {
  display: block;
  width: 11px;
  height: 11px;
}

.ix-main__meta {
  margin: 0.1rem 0 0;
  font-size: 0.7rem;
  color: #6b7280;
}

.ix-main__dot {
  margin: 0 0.25rem;
  color: #cbd5e1;
}

.ix-alert {
  margin: 0.75rem 1.25rem 0;
  border-radius: 10px;
}

/* ── Pills (reused everywhere) ───────────────────────────────────── */
.ix-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.ix-pill--blue   { background: #dbeafe; color: #1e40af; }
.ix-pill--amber  { background: #fef3c7; color: #92400e; }
.ix-pill--green  { background: #dff5ea; color: #0e5b46; }
.ix-pill--red    { background: #fee2e2; color: #991b1b; }
.ix-pill--gray   { background: #f1f5f9; color: #475569; }

.ix-sub {
  margin-left: 0.25rem;
  color: #6b7280;
  font-weight: 500;
}

/* ── Item list ───────────────────────────────────────────────────── */
.ix-list-card {
  background: #ffffff;
  /* Card chrome dropped — the white main pane is the canvas now. Rows
     are separated by the per-item border-bottom hairline. */
  overflow: hidden;
}

.ix-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ix-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem 0.85rem 0;
  border-bottom: 1px solid #eef1f7;
  cursor: pointer;
  transition: background 0.12s ease, box-shadow 0.12s ease;
  min-width: 0;
  position: relative;
}

.ix-item:last-child { border-bottom: 0; }

.ix-item:hover {
  background: #f8fafc;
}

.ix-item--open {
  background: #fffdf6;
}

.ix-item--open:hover {
  background: #fef9ec;
}

.ix-item__bar {
  flex: 0 0 auto;
  width: 3px;
  align-self: stretch;
  background: #e5e9f2;
}

.ix-item__bar--amber { background: linear-gradient(180deg, #f59e0b, #d97706); }
.ix-item__bar--green { background: linear-gradient(180deg, #16a07a, #0e5b46); }
.ix-item__bar--red   { background: linear-gradient(180deg, #ef4444, #b91c1c); }
.ix-item__bar--gray  { background: #cbd5e1; }

.ix-item__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-left: 0.5rem;
}

.ix-item__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.ix-item__title {
  font-size: 0.88rem;
  font-weight: 500;
  color: #1f2a44;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1 1 auto;
  letter-spacing: -0.01em;
}

.ix-item--open .ix-item__title {
  font-weight: 700;
}

.ix-item__status {
  flex-shrink: 0;
}

.ix-item__summary {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.ix-item--open .ix-item__summary {
  color: #4b5563;
}

.ix-item__aside {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.ix-item__time {
  font-size: 0.74rem;
  color: #6b7280;
  font-weight: 500;
  min-width: 56px;
  text-align: right;
  white-space: nowrap;
}

.ix-item--open .ix-item__time {
  color: #1f2a44;
  font-weight: 600;
}

.ix-item__action {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 30px;
  padding: 0 0.8rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
}

.ix-item__action:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}

.ix-list-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.ix-list-state--empty svg {
  color: #cbd5e1;
}

.ix-list-state p {
  margin: 0;
}

.ix-spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #e5e9f2;
  border-top-color: #16a07a;
  animation: ix-spin 0.7s linear infinite;
}

@keyframes ix-spin {
  to { transform: rotate(360deg); }
}

.ix-spinner--sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

/* Inline state lines under the order grid while the full order loads. */
.ix-od-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.2rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.ix-od-error {
  padding: 0.55rem 0.7rem;
  font-size: 0.8rem;
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.ix-cancelled-note {
  padding: 0.7rem 0.85rem;
  font-size: 0.82rem;
  color: #4b5563;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

/* ── Responsive ──────────────────────────────────────────────────── */
@media (max-width: 991px) {
  .ix-shell {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .ix-main__hamburger {
    display: inline-flex;
  }

  .ix-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 280px;
    height: 100vh;
    border-right: 1px solid #e5e9f2;
    box-shadow: 4px 0 16px rgba(15, 23, 42, 0.12);
    transform: translateX(-100%);
    transition: transform 0.22s ease;
    z-index: 999;
  }

  .ix-sidebar.is-open {
    transform: translateX(0);
  }

  .ix-sidebar__toggle {
    display: inline-flex;
  }

  .ix-sidebar__backdrop {
    display: block;
  }
}

@media (max-width: 720px) {
  .ix-item {
    flex-wrap: wrap;
    padding: 0.7rem 0.8rem 0.7rem 0;
  }
  .ix-item__body {
    flex: 1 1 100%;
    min-width: 0;
  }
  .ix-item__aside {
    flex: 1 1 100%;
    justify-content: space-between;
    padding-left: 0.5rem;
    margin-top: 0.35rem;
  }
  .ix-item__time {
    min-width: auto;
    text-align: left;
  }
}

@media (max-width: 575px) {
  /* Tighter page chrome — the main pane was eating ~30px to vertical
     header padding on phones, leaving little room for content above
     the fold. */
  .ix-main__head,
  .ix-sidebar__head {
    padding: 1.1rem 0.9rem 0.9rem;
  }

  .ix-main__title h2 { font-size: 0.95rem; }
  .ix-main__meta    { font-size: 0.7rem; }

  /* Drop the "Refresh" label on the smallest screens — icon alone keeps
     the header from wrapping next to the hamburger + title. */
  .ix-refresh-btn { padding: 0 0.55rem; }
  .ix-refresh-btn span { display: none; }

  .ix-alert { margin: 0.6rem 0.9rem 0; }

  /* Item rows: at this width the pill + title + status row was
     squeezing the title to a few characters. Drop the status pill
     onto its own line (alongside the timestamp in the aside) and let
     the title breathe across the full row. */
  .ix-item {
    padding: 0.65rem 0.8rem 0.65rem 0;
    gap: 0.55rem;
  }
  .ix-item__head {
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .ix-item__title {
    flex: 1 1 100%;
    white-space: normal;
    line-height: 1.3;
  }
  .ix-item__status { order: 2; }
  .ix-item__summary {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Modal: stack label/value vertically — 200px label column on a
     360px phone left almost nothing for the value. Mirrors the same
     fix in Orders.vue's order-detail modal. */
  .ix-modal >>> .modal-body { padding: 0.85rem; }
  .ix-modal >>> .modal-footer {
    flex-wrap: wrap;
    padding: 0.6rem 0.85rem;
  }
  .ix-btn { flex: 1 1 auto; min-width: 0; }

  .vo-row {
    grid-template-columns: 1fr;
    gap: 0.2rem;
    padding: 0.6rem 0.8rem;
  }
  .vo-grid { grid-template-columns: 1fr; }
  .vo-grid .vo-row {
    grid-template-columns: 1fr;
    border-right: 0;
  }
  .ix-note-wrap { padding: 0.75rem 0.8rem; }
}

/* ── Post Office complaint sub-form (payment-overdue reminder) ──── */
.ix-complaint {
  background: #ffffff;
  padding: 0.85rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.ix-complaint__head {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.ix-complaint__title {
  font-size: 0.86rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.ix-complaint__error {
  font-size: 0.78rem;
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.4rem 0.65rem;
}

.ix-complaint__field { display: flex; flex-direction: column; gap: 0.3rem; }

.ix-complaint__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.ix-complaint__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.7rem;
}

.ix-complaint__input { width: 100%; }

.ix-complaint__meta {
  font-size: 0.78rem;
  color: #4b5563;
  background: #f4fbf7;
  border: 1px solid #d6efe2;
  border-radius: 8px;
  padding: 0.4rem 0.65rem;
}

.ix-complaint__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.ix-complaint__copy {
  height: 26px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}

.ix-complaint__copy:hover { border-color: #16a07a; background: #f5fbf8; }

.ix-complaint__copy:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

@media (max-width: 576px) {
  .ix-complaint__grid { grid-template-columns: 1fr; }
}

/* ── Detail modal — order-detail vo-* design system (unchanged) ──── */
.ix-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.ix-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.ix-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.ix-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.ix-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.vo-body { display: flex; flex-direction: column; gap: 0.85rem; }

.vo-section,
.vo-grid {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

.vo-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 0.75rem;
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  align-items: center;
}

.vo-row:last-child { border-bottom: 0; }
.vo-section .vo-row:nth-child(even) { background: #fafbfd; }

.vo-key {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.vo-val {
  font-size: 0.82rem;
  color: #374151;
  word-break: break-word;
}

.vo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.vo-grid .vo-row {
  grid-template-columns: 140px 1fr;
  border-right: 1px solid #eef1f7;
}

.vo-grid .vo-row:nth-child(2n) { border-right: 0; }
.vo-grid .vo-row:nth-last-child(-n+2) { border-bottom: 0; }
.vo-grid .vo-row:nth-child(4n-1),
.vo-grid .vo-row:nth-child(4n) {
  background: #fafbfd;
}

/* Refund modal: long-form fields like "Reason" need the whole width. */
.vo-grid .vo-row--wide {
  grid-column: 1 / -1;
  grid-template-columns: 140px 1fr;
  border-right: 0;
}

/* Proof preview block — shared visual with Refunds.vue. */
.vo-section--proof {
  background: #ffffff;
  padding: 0;
}

.rf-proof-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.rf-proof-title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.rf-proof-open {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.32rem 0.7rem;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #0e5b46;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.rf-proof-open:hover {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.rf-proof-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.85rem;
  background: #f8fafc;
  min-height: 220px;
}

.rf-proof-image {
  display: block;
  max-width: 100%;
  max-height: 520px;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.rf-proof-pdf {
  display: block;
  width: 100%;
  height: 520px;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
}

.rf-proof-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 1.5rem;
  font-size: 0.82rem;
  color: #4b5563;
  text-align: center;
}

.rf-proof-fallback a {
  color: #0e5b46;
  font-weight: 600;
  text-decoration: underline;
}

/* Written payment-proof note — staff's alternative to a screenshot */
.pd-proof-note {
  margin: 0;
  padding: 0.85rem 0.95rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #1f2a44;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Payment-screenshot gallery — multiple proofs as click-to-open thumbnails */
.pd-proof-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 0.6rem;
  padding: 0.85rem;
  background: #f8fafc;
}

.pd-proof-cell {
  position: relative;
}

.pd-proof-link {
  display: block;
  position: relative;
  width: 100%;
  padding: 0;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pd-proof-link:hover {
  border-color: #16a07a;
  box-shadow: 0 2px 8px rgba(14, 91, 70, 0.14);
}

.pd-proof-link:focus-visible {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.pd-proof-thumb {
  display: block;
  width: 100%;
  height: 110px;
  object-fit: cover;
  background: #ffffff;
}

.pd-proof-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  height: 110px;
  padding: 0.5rem;
  text-align: center;
}

.pd-proof-pdf__badge {
  font-size: 0.68rem;
  font-weight: 700;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
}

.pd-proof-pdf__name {
  font-size: 0.7rem;
  color: #4b5563;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pd-proof-open {
  position: absolute;
  bottom: 0.35rem;
  right: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  color: #ffffff;
  background: rgba(15, 23, 42, 0.55);
}

.pd-proof-remove {
  position: absolute;
  top: 0.3rem;
  right: 0.3rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 1rem;
  line-height: 1;
  color: #ffffff;
  background: rgba(185, 28, 28, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}

.pd-proof-remove:hover:not(:disabled) {
  background: #b91c1c;
  transform: scale(1.05);
}

.pd-proof-remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.35);
}

.pd-proof-remove:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pd-proof-empty {
  margin: 0;
  padding: 0.85rem;
  font-size: 0.82rem;
  color: #6b7280;
  background: #f8fafc;
}

.pd-proof-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 0.85rem;
  border-top: 1px solid #eef1f7;
}

.pd-proof-file {
  display: none;
}

.pd-proof-add {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.pd-proof-error {
  margin: 0;
  padding: 0 0.85rem 0.7rem;
  font-size: 0.78rem;
  color: #b91c1c;
}

/* ── Screenshot gallery lightbox ─────────────────────────────────────── */
.ix-gallery {
  position: fixed;
  inset: 0;
  z-index: 2000; /* above CoreUI modal (~1050) */
  display: flex;
  flex-direction: column;
  background: rgba(8, 13, 24, 0.88);
  backdrop-filter: blur(2px);
}

.ix-gallery__bar {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.7rem 1rem;
  color: #f1f5f9;
  flex-shrink: 0;
}

.ix-gallery__title {
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50%;
}

.ix-gallery__count {
  font-size: 0.8rem;
  font-weight: 600;
  color: #cbd5e1;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  padding: 0.15rem 0.6rem;
}

.ix-gallery__action {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #e2e8f0;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  padding: 0.35rem 0.7rem;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.ix-gallery__action:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
}

.ix-gallery__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.ix-gallery__close:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.ix-gallery__action:focus-visible,
.ix-gallery__close:focus-visible,
.ix-gallery__nav:focus-visible,
.ix-gallery__thumb:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.55);
}

.ix-gallery__stage {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 0.5rem 0.5rem;
  min-height: 0;
}

.ix-gallery__nav {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}

.ix-gallery__nav:hover {
  background: rgba(255, 255, 255, 0.24);
  transform: scale(1.06);
}

.ix-gallery__viewport {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.ix-gallery__image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  background: #ffffff;
}

.ix-gallery__frame {
  width: min(900px, 100%);
  height: 100%;
  border: none;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
}

.ix-gallery__strip {
  display: flex;
  gap: 0.5rem;
  padding: 0.7rem 1rem 1rem;
  overflow-x: auto;
  flex-shrink: 0;
  justify-content: center;
}

.ix-gallery__thumb {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s ease, border-color 0.15s ease;
}

.ix-gallery__thumb:hover {
  opacity: 0.9;
}

.ix-gallery__thumb.is-active {
  opacity: 1;
  border-color: #16a07a;
}

.ix-gallery__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ix-gallery__thumb-pdf {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 0.7rem;
  font-weight: 700;
  color: #fca5a5;
}

.ix-gallery-fade-enter-active,
.ix-gallery-fade-leave-active {
  transition: opacity 0.18s ease;
}

.ix-gallery-fade-enter,
.ix-gallery-fade-leave-to {
  opacity: 0;
}

.ix-note-wrap {
  padding: 0.85rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.ix-note-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  margin: 0;
}

.ix-note-input {
  width: 100%;
  min-height: 60px;
  padding: 0.5rem 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  font-family: inherit;
  color: #2f3640;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.ix-note-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.ix-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  min-width: 90px;
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.ix-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  filter: none;
  transform: none;
  box-shadow: none;
}

.ix-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.ix-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #d8dde6;
}

.ix-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.ix-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.ix-btn--danger {
  background: #ffffff;
  border: 1px solid #fecaca;
  color: #b91c1c;
}

.ix-btn--danger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #ef4444;
}

/* ── Payment Approvals view ──────────────────────────────────────── */
.ix-pay {
  padding: 1rem 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ix-pay-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 1rem;
}

.ix-pay-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 200px;
  min-width: 0;
}

.ix-pay-filter--action {
  flex: 0 0 auto;
  min-width: 120px;
}

.ix-pay-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.ix-pay-filter--action .ix-btn {
  height: 36px;
  width: 100%;
}

/* Row actions inside the BaseTable */
.ix-pay-row-actions {
  display: inline-flex;
  gap: 0.4rem;
  justify-content: flex-end;
}

.ix-pay-act {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}

.ix-pay-act svg { flex-shrink: 0; }

.ix-pay-act--approve {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.ix-pay-act--approve:hover {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.ix-pay-act:focus-visible {
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Payment detail modal (reuses .ix-modal vo-* chrome) ─────────── */
.ix-pd-error {
  padding: 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  color: #b91c1c;
}

.vo-items-wrap {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

.vo-items {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.vo-items thead th {
  text-align: left;
  font-weight: 700;
  color: #1f2a44;
  background: #fbfcfe;
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid #eef1f7;
}

.vo-items tbody td {
  padding: 0.5rem 0.85rem;
  border-bottom: 1px solid #f1f5f9;
  color: #374151;
}

.vo-items tbody tr:last-child td { border-bottom: 0; }
.vo-items th:not(:first-child),
.vo-items td:not(:first-child) { text-align: right; width: 90px; }

.vo-items__empty {
  text-align: center !important;
  color: #94a3b8;
  padding: 0.9rem !important;
}

/* Inline payment-method update inside the detail modal */
.ix-pd-method__control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ix-pd-method__control >>> .app-dropdown {
  flex: 1 1 auto;
  min-width: 0;
}

.ix-pd-method__save {
  flex: 0 0 auto;
  height: 36px;
  min-width: 86px;
}

.ix-pd-method__error {
  padding: 0 0.95rem 0.7rem;
  font-size: 0.78rem;
  color: #b91c1c;
}

.ix-pd-req { color: #dc2626; }

.ix-pd-date__control { display: flex; }

.ix-pd-date__control >>> .single-date-picker,
.ix-pd-date__control >>> .sdp,
.ix-pd-date__control > * { flex: 1 1 auto; min-width: 0; }

.ix-pd-date__hint {
  margin: 0;
  padding: 0 0.95rem 0.7rem;
  font-size: 0.74rem;
  color: #6b7280;
}
</style>
